"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { Calendar, Check, Clock, PhoneCall, Send, User, Video, X } from "lucide-react";
import dynamic from "next/dynamic";

// Der Terminkalender (groß) wird erst geladen, wenn der Reiter geöffnet wird –
// so bleibt er aus dem Start-Bundle der Startseite heraus.
const BookingFlow = dynamic(() => import("@/app/beratung/BookingFlow").then((mod) => mod.BookingFlow), {
  ssr: false,
  loading: () => (
    <p className="py-6 text-center text-sm" style={{ color: "var(--text-tertiary)" }} role="status">
      Terminkalender wird geladen …
    </p>
  ),
});
import { LandingInquiryForm } from "./landing-inquiry-form";
import { PcbootAdvantageCard } from "./landing-pcboot";
import {
  HOURS_LINES,
  LEADERSHIP,
  PHONE_DISPLAY,
  PHONE_TEL,
  SectionHeading,
  type LandingBookingType,
  type LandingReferrer,
} from "./landing-shared";

/* ─────────────────────────────────────────────────────────────
 * Kontaktbereich: zwei Wege, ein Ort.
 *
 *   • „Rückruf anfragen“ – das kurze Formular (3 Pflichtfelder). Niedrigste
 *     Hürde, Standard-Reiter: passt zu Besuchern, die erst reden wollen.
 *   • „Termin direkt wählen“ – derselbe Buchungsablauf wie auf /beratung,
 *     hier in der Sie-Form. Für Entschlossene: fester Termin statt Warten
 *     auf einen Rückruf, ohne die Seite zu verlassen.
 *
 * Beide Anker führen hierher: #anfrage öffnet das Formular, #termin den
 * Kalender (Hero-Button, Header, Fallstudien-CTA). Das Buchungs-Widget
 * wird erst beim ersten Öffnen geladen – es fragt beim Start freie Zeiten
 * ab, das soll nicht bei jedem Seitenaufruf passieren.
 * ──────────────────────────────────────────────────────────── */

type Mode = "form" | "booking";

export function LandingContact({
  referrer,
  bookingTypes,
  source,
}: {
  referrer: LandingReferrer;
  bookingTypes: LandingBookingType[];
  /** Lead-Herkunft im Admin, z. B. „Startseite“. Ohne Angabe: Landingpage-Logik. */
  source?: string;
}) {
  const isPcboot = referrer === "pcboot";
  const canBook = bookingTypes.length > 0;
  const [mode, setMode] = useState<Mode>("form");
  const [bookingOpened, setBookingOpened] = useState(false);

  useEffect(() => {
    const sync = () => {
      if (window.location.hash === "#termin" && canBook) {
        setMode("booking");
        setBookingOpened(true);
      } else if (window.location.hash === "#anfrage") {
        setMode("form");
      }
    };
    sync();
    window.addEventListener("hashchange", sync);

    // Steht der Anker schon in der Adresse, löst ein erneuter Klick kein
    // hashchange aus (z. B. erst „Termin“, dann Reiter gewechselt, dann wieder
    // der Termin-Button im Header). Deshalb zusätzlich auf die Klicks hören.
    const onClick = (e: MouseEvent) => {
      const link = (e.target as HTMLElement | null)?.closest?.('a[href="#termin"], a[href="#anfrage"]');
      if (!link) return;
      if (link.getAttribute("href") === "#termin" && canBook) {
        setMode("booking");
        setBookingOpened(true);
      } else if (link.getAttribute("href") === "#anfrage") {
        setMode("form");
      }
    };
    document.addEventListener("click", onClick);

    return () => {
      window.removeEventListener("hashchange", sync);
      document.removeEventListener("click", onClick);
    };
  }, [canBook]);

  const openBooking = () => {
    setMode("booking");
    setBookingOpened(true);
  };

  const tabs: { key: Mode; label: string; Icon: typeof Send }[] = [
    { key: "form", label: "Rückruf anfragen", Icon: Send },
    { key: "booking", label: "Termin direkt wählen", Icon: Calendar },
  ];

  return (
    <section
      id="anfrage"
      className="py-16 lg:py-24"
      style={{ scrollMarginTop: "72px", backgroundColor: "var(--bg-secondary)" }}
    >
      {/* Zweiter Anker für die Termin-Buttons – gleiche Stelle, anderer Reiter. */}
      <span id="termin" className="block" style={{ scrollMarginTop: "72px" }} aria-hidden="true" />
      <div className="mx-auto max-w-6xl px-4 lg:px-8">
        <SectionHeading
          eyebrow="Der einfachste nächste Schritt"
          title={canBook ? "Kurz schreiben oder direkt einen Termin wählen." : "Sagen Sie uns kurz, worum es geht."}
          text="Kostenlos, unverbindlich, ohne Verkaufsdruck. Sie schildern kurz Ihre Situation – wir sagen ehrlich, ob und wie wir helfen können. Wenn nicht, empfehlen wir jemanden, der besser passt."
        />

        <div className="mx-auto mt-10 grid max-w-5xl items-start gap-8 lg:grid-cols-5 lg:gap-10">
          {/* Linke Spalte: Telefon + Vertrauen */}
          <div className="lg:col-span-2">
            {isPcboot && (
              <div className="mb-6">
                <PcbootAdvantageCard compact />
              </div>
            )}
            <div className="rounded-2xl border p-6" style={{ borderColor: "var(--fi-brand-fg)", backgroundColor: "var(--fi-brand-bg)" }}>
              <div className="flex items-center gap-4">
                <div className="flex -space-x-3">
                  {LEADERSHIP.map((m) => (
                    <Image
                      key={m.name}
                      src={m.image}
                      alt={m.name}
                      width={56}
                      height={56}
                      className="h-14 w-14 rounded-full object-cover"
                      style={{ border: "3px solid var(--card-bg)", boxShadow: "var(--card-shadow-md)" }}
                    />
                  ))}
                </div>
                <div>
                  <h3 className="text-lg font-semibold" style={{ color: "var(--text)" }}>
                    Lieber direkt sprechen?
                  </h3>
                  <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
                    Sie erreichen unser Team in Salz – keine Hotline.
                  </p>
                </div>
              </div>
              <a
                href={`tel:${PHONE_TEL}`}
                className="mt-5 flex items-center gap-3 text-2xl font-bold tracking-tight text-brand-500 hover:underline sm:text-3xl"
              >
                <PhoneCall className="h-6 w-6 shrink-0" />
                {PHONE_DISPLAY}
              </a>
              <div className="mt-1.5 text-sm" style={{ color: "var(--text-secondary)" }}>
                {HOURS_LINES.map((l) => (
                  <p key={l}>{l}</p>
                ))}
                <p className="mt-1">Rückruf am selben Werktag, wenn Sie uns nicht erreichen.</p>
              </div>
              {!canBook && (
                <Link
                  href="/beratung"
                  className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold hover:underline"
                  style={{ color: "var(--fi-brand-fg)" }}
                >
                  <Calendar className="h-4 w-4" />
                  Oder Gesprächstermin online wählen
                </Link>
              )}
            </div>

            <ul className="mt-6 space-y-3">
              {(mode === "booking"
                ? [
                    { Icon: Clock, text: "30 bis 60 Minuten, kostenlos und unverbindlich" },
                    { Icon: Video, text: "Online via Google Meet oder per Telefon" },
                    { Icon: Check, text: "Bestätigung per Mail, stornieren mit einem Klick" },
                  ]
                : [
                    { Icon: Check, text: "Rückruf am selben Werktag" },
                    { Icon: User, text: "Fester Ansprechpartner aus der Geschäftsführung" },
                    { Icon: X, text: "Kein Newsletter, kein Spam" },
                  ]
              ).map((t) => {
                const Icon = t.Icon;
                return (
                  <li key={t.text} className="flex items-center gap-2.5 text-sm" style={{ color: "var(--text-secondary)" }}>
                    <span
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
                      style={{ backgroundColor: "var(--fi-brand-bg)", color: "var(--fi-brand-fg)" }}
                    >
                      <Icon className="h-3.5 w-3.5" />
                    </span>
                    {t.text}
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Rechte Spalte: Reiter + Formular oder Kalender */}
          <div className="lg:col-span-3">
            {canBook && (
              <div
                role="tablist"
                aria-label="Kontaktweg wählen"
                className="mb-4 grid grid-cols-2 gap-1 rounded-xl border p-1"
                style={{ borderColor: "var(--border)", backgroundColor: "var(--card-bg)" }}
              >
                {tabs.map(({ key, label, Icon }) => {
                  const active = mode === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      onClick={() => (key === "booking" ? openBooking() : setMode("form"))}
                      className="flex items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors"
                      style={
                        active
                          ? { backgroundColor: "var(--color-brand-700)", color: "white" }
                          : { color: "var(--text-secondary)" }
                      }
                    >
                      <Icon className="h-4 w-4" />
                      {label}
                    </button>
                  );
                })}
              </div>
            )}

            <div className={mode === "form" ? "" : "hidden"}>
              <LandingInquiryForm referrer={referrer} source={source} />
            </div>
            {canBook && bookingOpened && (
              <div className={mode === "booking" ? "" : "hidden"}>
                <BookingFlow types={bookingTypes} />
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
